import { zodResolver } from '@hookform/resolvers/zod';
import { pageTitle } from '@palaccount/config';
import type { CreatePartnerRequest, PartnerResponse } from '@palaccount/contracts';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { localizeServerMessage } from '@/lib/server-messages';
import { useBaseCurrency } from '@/lib/session';

import { useOrgSetting } from '@/features/admin/api';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { usePaymentTerms } from '@/features/admin/payment-terms-api';
import { useCurrencyPickerCodes } from '@/features/fx/api';
import { usePriceTiers } from '@/features/inventory/api';

import { useCreatePartner, usePartner, useUpdatePartner } from './api';
import { PartnerBranchesCard } from './partner-branches-card';
import { usePartnerGroups } from './groups-api';
import { PAYMENT_METHODS, partnerMethodKey } from './constants';

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INT_RE = /^\d+$/;

/**
 * The form speaks strings (what inputs hold); the contract request is built
 * from it on submit with empties pruned. Messages are i18n keys, translated
 * at render — the sign-up page's pattern.
 */
const PartnerFormSchema = z
  .object({
    name: z.string().trim().min(1, 'partners.errName').max(120, 'partners.errName'),
    isCustomer: z.boolean(),
    isSupplier: z.boolean(),
    /** MD-01 field 4 — a PICK from the groups list; '' = no group. */
    partnerGroupId: z.string(),
    taxId: z.string().trim().max(40),
    primaryContact: z.string().trim().max(120),
    phone: z.string().trim().max(30),
    email: z.union([z.literal(''), z.string().regex(EMAIL_RE, 'partners.errEmail')]),
    billingStreet: z.string().trim().max(120),
    billingCity: z.string().trim().max(60),
    billingRegion: z.string().trim().max(60),
    billingPostalCode: z.string().trim().max(20),
    billingCountry: z.union([z.literal(''), z.string().regex(/^[A-Z]{2}$/, 'partners.errCountry')]),
    shippingSameAsBilling: z.boolean(),
    shippingStreet: z.string().trim().max(120),
    shippingCity: z.string().trim().max(60),
    shippingRegion: z.string().trim().max(60),
    shippingPostalCode: z.string().trim().max(20),
    shippingCountry: z.union([z.literal(''), z.string().regex(/^[A-Z]{2}$/, 'partners.errCountry')]),
    /**
     * MD-01 field 21 — the money this partner is billed in (GEN-108).
     *
     * A `z.enum(CURRENCIES)` until the picker started reading the tenant's own
     * table. A dynamic list cannot be a `z.enum`, so this field's VALIDATION
     * CONTRACT changed with it: the SHAPE rule (three capitals) is enforced
     * here, and WHICH three is the server's to judge — `currency-guard.ts`
     * holds BR-DATA-06 and is the only place that can, because only the server
     * knows what the tenant switched off a second ago. Widening this was the
     * point rather than a side effect: the enum was refusing codes the company
     * legitimately owned, and coercing them (see `fromPartner`) when it could.
     */
    currency: z
      .string()
      .trim()
      .regex(/^[A-Z]{3}$/, 'partners.errCurrency'),
    /** MD-01 field 22 — a PICK from the MD-09 list; '' = no term (due on issue). */
    paymentTermId: z.string(),
    creditLimit: z.union([z.literal(''), z.string().regex(MONEY_RE, 'partners.errMoney')]),
    creditEnforcement: z.enum(['block', 'warn', 'none']),
    discountPct: z.union([z.literal(''), z.string().regex(PCT_RE, 'partners.errPct')]),
    /** MD-01 field 26 — a PICK from MD-14's list; '' = no tier (unit price). */
    priceTierId: z.string(),
    paymentMethod: z.string().max(40),
    leadTimeDays: z.union([z.literal(''), z.string().regex(INT_RE, 'partners.errDays')]),
    openingBalance: z.union([z.literal(''), z.string().regex(MONEY_RE, 'partners.errMoney')]),
    notes: z.string().max(2000),
  })
  // BR-MD-01-05a is handled in the tick handlers (silent re-tick); this is
  // the backstop so an impossible state can never be submitted.
  .refine((value) => value.isCustomer || value.isSupplier, {
    message: 'partners.errNoType',
    path: ['isCustomer'],
  });

type FormValues = z.infer<typeof PartnerFormSchema>;

const EMPTY: FormValues = {
  name: '',
  isCustomer: true,
  isSupplier: false,
  partnerGroupId: '',
  taxId: '',
  primaryContact: '',
  phone: '',
  email: '',
  billingStreet: '',
  billingCity: '',
  billingRegion: '',
  billingPostalCode: '',
  billingCountry: '',
  shippingSameAsBilling: true,
  shippingStreet: '',
  shippingCity: '',
  shippingRegion: '',
  shippingPostalCode: '',
  shippingCountry: '',
  /**
   * EMPTY, not a placeholder code. It used to be `CURRENCIES[0]` — literally
   * `'USD'` — because `z.enum(CURRENCIES)` demanded a member. That placeholder
   * stopped being harmless the moment the effect below could decline to
   * replace it: a CAD-base company got a new partner denominated in dollars
   * and no message saying so. An empty string cannot be mistaken for an
   * answer, and the effect fills it on the same tick.
   */
  currency: '',
  paymentTermId: '',
  creditLimit: '',
  creditEnforcement: 'warn',
  discountPct: '',
  priceTierId: '',
  paymentMethod: '',
  leadTimeDays: '',
  openingBalance: '',
  notes: '',
};

function fromPartner(partner: PartnerResponse): FormValues {
  return {
    name: partner.name,
    isCustomer: partner.isCustomer,
    isSupplier: partner.isSupplier,
    partnerGroupId: partner.partnerGroupId ?? '',
    taxId: partner.taxId ?? '',
    primaryContact: partner.primaryContact ?? '',
    phone: partner.phone ?? '',
    email: partner.email ?? '',
    billingStreet: partner.billingStreet ?? '',
    billingCity: partner.billingCity ?? '',
    billingRegion: partner.billingRegion ?? '',
    billingPostalCode: partner.billingPostalCode ?? '',
    billingCountry: partner.billingCountry ?? '',
    shippingSameAsBilling: partner.shippingSameAsBilling,
    shippingStreet: partner.shippingStreet ?? '',
    shippingCity: partner.shippingCity ?? '',
    shippingRegion: partner.shippingRegion ?? '',
    shippingPostalCode: partner.shippingPostalCode ?? '',
    shippingCountry: partner.shippingCountry ?? '',
    /**
     * THE PARTNER'S OWN CURRENCY, verbatim.
     *
     * This used to coerce anything outside a hard-coded six to the LITERAL
     * `'USD'`, which made a rendering convenience into a silent write: a CAD
     * partner (reachable by import or API) opened showing USD, and saving an
     * unrelated edit — a phone number — PATCHed `currency: 'USD'` over it.
     * Either a silent re-denomination of every future document raised for that
     * partner (GEN-108 copies this code onto each one) or a BR-MD-01-05 lock
     * refusal the user never provoked. Nothing on screen said the currency had
     * changed, which is what made it the worst of the three defects here.
     */
    currency: partner.currency,
    paymentTermId: partner.paymentTermId ?? '',
    creditLimit: partner.creditLimit ?? '',
    creditEnforcement: partner.creditEnforcement,
    discountPct: partner.discountPct,
    priceTierId: partner.priceTierId ?? '',
    paymentMethod: partner.paymentMethod ?? '',
    leadTimeDays: partner.leadTimeDays === null ? '' : String(partner.leadTimeDays),
    openingBalance: partner.openingBalance,
    notes: partner.notes ?? '',
  };
}

/** Builds the contract body, pruning empty optionals. */
function toRequest(values: FormValues): CreatePartnerRequest {
  const text = (value: string) => (value.trim() === '' ? undefined : value.trim());
  return {
    name: values.name.trim(),
    isCustomer: values.isCustomer,
    isSupplier: values.isSupplier,
    partnerGroupId: values.partnerGroupId === '' ? undefined : values.partnerGroupId,
    taxId: text(values.taxId),
    primaryContact: text(values.primaryContact),
    phone: text(values.phone),
    email: text(values.email),
    billingStreet: text(values.billingStreet),
    billingCity: text(values.billingCity),
    billingRegion: text(values.billingRegion),
    billingPostalCode: text(values.billingPostalCode),
    billingCountry: text(values.billingCountry),
    shippingSameAsBilling: values.shippingSameAsBilling,
    shippingStreet: text(values.shippingStreet),
    shippingCity: text(values.shippingCity),
    shippingRegion: text(values.shippingRegion),
    shippingPostalCode: text(values.shippingPostalCode),
    shippingCountry: text(values.shippingCountry),
    currency: values.currency,
    paymentTermId: values.paymentTermId === '' ? undefined : values.paymentTermId,
    creditLimit: text(values.creditLimit),
    creditEnforcement: values.creditEnforcement,
    discountPct: text(values.discountPct),
    priceTierId: values.priceTierId === '' ? undefined : values.priceTierId,
    paymentMethod: text(values.paymentMethod),
    leadTimeDays: values.leadTimeDays === '' ? undefined : Number(values.leadTimeDays),
    openingBalance: text(values.openingBalance),
    notes: text(values.notes),
  };
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? <p className="text-sm text-destructive">{t(error)}</p> : null}
    </div>
  );
}

/**
 * MD-01 §5 — one form for + New and Edit, sectioned as the spec's tabs
 * (General / Addresses / Financial / Notes) in a single scrolling column, the
 * slice's stand-in for real tabs. The Code box is never typed: it reads "New"
 * until the server mints BP-#### (§13).
 */
export function PartnerFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const isEdit = Boolean(partnerId);
  const { data: existing, isPending: loadingExisting } = usePartner(partnerId);
  const createPartner = useCreatePartner();
  const updatePartner = useUpdatePartner(partnerId ?? '');
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t(isEdit ? 'partners.editTitle' : 'partners.newTitle'));
  }, [t, isEdit]);

  const form = useForm<FormValues>({
    resolver: zodResolver(PartnerFormSchema),
    defaultValues: EMPTY,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (existing) form.reset(fromPartner(existing));
  }, [existing, form]);

  /**
   * MD-01 field 21, in its own words: "Defaults to the base currency."
   *
   * `EMPTY` is a module constant and cannot read a session, so the seed
   * happens here — the same shape as the payment-term prefill below, and for
   * the same reason: it fills a blank on a NEW form and never overwrites a
   * choice or touches an edit. The literal it replaces is not cosmetic on
   * this screen: GEN-108 then copies this code onto every document raised
   * for the partner, and BR-MD-01-05 locks it once one exists.
   */
  const baseCurrency = useBaseCurrency();
  const currencyTouched = form.formState.dirtyFields.currency === true;
  useEffect(() => {
    if (isEdit) return;
    /*
     * THE TEST IS "HAS THE USER TOUCHED IT", NOT "IS IT STILL EMPTY" — and the
     * difference is a race this screen loses every time without it.
     *
     * `useBaseCurrency()` answers `'USD'` while the session is still in
     * flight, because a hook that returns a string cannot say "I do not know
     * yet". The old guard stamped that placeholder on the first render and
     * then refused to touch the field again, since it was no longer empty. A
     * CAD company therefore got `USD` — not from the hard-coded list this time
     * but from the fallback, which is the same defect wearing a new hat.
     *
     * Keyed on `dirtyFields` instead, the seed is free to correct itself when
     * the real base arrives, and stops the moment the user expresses a
     * preference. `setValue` without `shouldDirty` does not count as touching.
     *
     * NO `CURRENCIES.includes` GUARD either. It used to bail out for any base
     * outside the hard-coded six — GEN-107 says the tenant has exactly one
     * base, and this screen decided it was not one of the allowed ones.
     */
    if (currencyTouched) return;
    if (form.getValues('currency') === baseCurrency) return;
    form.setValue('currency', baseCurrency);
  }, [isEdit, baseCurrency, currencyTouched, form]);

  /*
   * BR-DATA-06 + BR-MD-01-07, in one call: the tenant's own live codes, plus
   * whatever this partner already holds so a locked currency the company has
   * since switched off still displays and still saves.
   */
  const currencyCodes = useCurrencyPickerCodes(form.watch('currency'));

  const errors = form.formState.errors;
  const isCustomer = form.watch('isCustomer');
  const isSupplier = form.watch('isSupplier');
  // MD-09 §9 — with the switch off, no term is asked for anywhere.
  const paymentTermsOn = useOrgSetting<boolean>('invoicing.payment_terms');
  const { data: paymentTerms } = usePaymentTerms(true);
  const { data: partnerGroups } = usePartnerGroups(true);
  const { data: priceTiers } = usePriceTiers(true);

  // MD-09 fields 7–8: the default term "is filled in on every new customer"
  // (or vendor) — a form prefill, freely changeable, never applied on edit.
  useEffect(() => {
    if (isEdit || !paymentTermsOn || !paymentTerms) return;
    if (form.getValues('paymentTermId') !== '') return;
    const wanted = isCustomer
      ? paymentTerms.find((term) => term.isActive && term.defaultForCustomers)
      : paymentTerms.find((term) => term.isActive && term.defaultForVendors);
    if (wanted) form.setValue('paymentTermId', wanted.id);
    // Deliberately not depending on the user's later edits — this runs to
    // seed an EMPTY field on a new form and never overwrites a choice.
    // (No eslint-disable: react-hooks/exhaustive-deps is not registered in
    // this workspace's flat config, and naming an unknown rule IS the error.)
  }, [isEdit, paymentTermsOn, paymentTerms, isCustomer]);
  const shippingSame = form.watch('shippingSameAsBilling');
  const name = form.watch('name');
  const submitting = form.formState.isSubmitting;

  /** BR-MD-01-05a: unticking the last remaining type silently re-ticks it. */
  const setType = (field: 'isCustomer' | 'isSupplier', checked: boolean) => {
    if (!checked) {
      const other = field === 'isCustomer' ? isSupplier : isCustomer;
      if (!other) {
        form.setValue(field, true);
        return;
      }
    }
    form.setValue(field, checked);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      if (isEdit) {
        const result = await updatePartner.mutateAsync(toRequest(values));
        void navigate(`/partners/${partnerId}`, { state: { warnings: result.warnings } });
      } else {
        const result = await createPartner.mutateAsync(toRequest(values));
        void navigate(`/partners/${result.partner.id}`, { state: { warnings: result.warnings } });
      }
    } catch (error) {
      // The cause rides along: a structured refusal (D46 — taxIdTaken,
      // discountTooHigh's live cap, mustStayCustomer, currencyLocked) carries
      // key+params for the Arabic reader.
      setServerError(
        localizeServerMessage(t, error instanceof Error ? error.message : String(error), error),
      );
    }
  });

  if (isEdit && loadingExisting) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  return (
    <form noValidate onSubmit={onSubmit} className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold">
          {t(isEdit ? 'partners.editTitle' : 'partners.newTitle')}
        </h2>
        {/* MD-01 §5 field 1: greyed out, never typed. */}
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {isEdit && existing ? <bdi dir="ltr">{existing.code}</bdi> : t('partners.codeNew')}
        </span>
        <div className="ms-auto flex gap-2">
          <Button asChild variant="outline">
            <Link to={isEdit ? `/partners/${partnerId}` : '/partners'}>{t('common.cancel')}</Link>
          </Button>
          {/* MD-01 §6: Save is greyed out until Name has a value. */}
          <Button type="submit" disabled={!name.trim() || submitting}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden /> : null}
            {t('common.save')}
          </Button>
        </div>
      </div>

      {serverError ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {serverError}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('partners.secGeneral')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field id="p-name" label={t('partners.fName')} error={errors.name?.message}>
            <Input id="p-name" maxLength={120} aria-invalid={Boolean(errors.name)} {...form.register('name')} />
          </Field>
          <div className="grid gap-1.5">
            <span className="text-sm font-medium leading-none">{t('partners.fTypes')}</span>
            <div className="flex h-12 items-center gap-6">
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={isCustomer}
                  onCheckedChange={(checked) => setType('isCustomer', checked === true)}
                />
                {t('partners.typeCustomer')}
              </Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={isSupplier}
                  onCheckedChange={(checked) => setType('isSupplier', checked === true)}
                />
                {t('partners.typeSupplier')}
              </Label>
            </div>
          </div>
          {/* MD-01 field 4 — "Pick from list (Partner groups)". Picking a
              group whose record carries a default term prefills the term
              field ("used for … default terms"); the pick stays editable. */}
          <Field id="p-group" label={t('partners.fGroup')}>
            <NativeSelect
              id="p-group"
              {...form.register('partnerGroupId', {
                onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
                  const picked = (partnerGroups ?? []).find(
                    (row) => row.id === event.target.value,
                  );
                  if (picked?.defaultPaymentTermId) {
                    form.setValue('paymentTermId', picked.defaultPaymentTermId);
                  }
                },
              })}
            >
              <option value="">{t('partners.groupNone')}</option>
              {(partnerGroups ?? [])
                .filter((row) => row.isActive || row.id === form.watch('partnerGroupId'))
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
            </NativeSelect>
          </Field>
          <Field id="p-taxid" label={t('partners.fTaxId')}>
            <Input id="p-taxid" dir="ltr" maxLength={40} {...form.register('taxId')} />
          </Field>
          <Field id="p-contact" label={t('partners.fContact')}>
            <Input id="p-contact" maxLength={120} {...form.register('primaryContact')} />
          </Field>
          <Field id="p-phone" label={t('partners.fPhone')}>
            {/* MD-01 field 7: always displayed left-to-right. */}
            <Input id="p-phone" dir="ltr" maxLength={30} {...form.register('phone')} />
          </Field>
          <Field id="p-email" label={t('partners.fEmail')} error={errors.email?.message}>
            <Input id="p-email" type="email" dir="ltr" maxLength={120} aria-invalid={Boolean(errors.email)} {...form.register('email')} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('partners.secAddresses')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="p-bstreet" label={t('partners.fStreet')}>
              <Input id="p-bstreet" maxLength={120} {...form.register('billingStreet')} />
            </Field>
            <Field id="p-bcity" label={t('partners.fCity')}>
              <Input id="p-bcity" maxLength={60} {...form.register('billingCity')} />
            </Field>
            <Field id="p-bregion" label={t('partners.fRegion')}>
              <Input id="p-bregion" maxLength={60} {...form.register('billingRegion')} />
            </Field>
            <Field id="p-bpostal" label={t('partners.fPostal')}>
              {/* MD-01 field 13: always left-to-right. */}
              <Input id="p-bpostal" dir="ltr" maxLength={20} {...form.register('billingPostalCode')} />
            </Field>
            <Field id="p-bcountry" label={t('partners.fCountry')} error={errors.billingCountry?.message}>
              <Input
                id="p-bcountry"
                dir="ltr"
                maxLength={2}
                className="uppercase"
                aria-invalid={Boolean(errors.billingCountry)}
                {...form.register('billingCountry', {
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    form.setValue('billingCountry', event.target.value.toUpperCase());
                  },
                })}
              />
            </Field>
          </div>
          <Label className="flex items-center gap-2 font-normal">
            <Checkbox
              checked={shippingSame}
              onCheckedChange={(checked) => form.setValue('shippingSameAsBilling', checked === true)}
            />
            {t('partners.fShippingSame')}
          </Label>
          {/* MD-01 field 15: hidden while the tick box is ticked. */}
          {!shippingSame ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="p-sstreet" label={t('partners.fShipStreet')}>
                <Input id="p-sstreet" maxLength={120} {...form.register('shippingStreet')} />
              </Field>
              <Field id="p-scity" label={t('partners.fCity')}>
                <Input id="p-scity" maxLength={60} {...form.register('shippingCity')} />
              </Field>
              <Field id="p-sregion" label={t('partners.fRegion')}>
                <Input id="p-sregion" maxLength={60} {...form.register('shippingRegion')} />
              </Field>
              <Field id="p-spostal" label={t('partners.fPostal')}>
                <Input id="p-spostal" dir="ltr" maxLength={20} {...form.register('shippingPostalCode')} />
              </Field>
              <Field id="p-scountry" label={t('partners.fCountry')} error={errors.shippingCountry?.message}>
                <Input
                  id="p-scountry"
                  dir="ltr"
                  maxLength={2}
                  className="uppercase"
                  aria-invalid={Boolean(errors.shippingCountry)}
                  {...form.register('shippingCountry', {
                    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                      form.setValue('shippingCountry', event.target.value.toUpperCase());
                    },
                  })}
                />
              </Field>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('partners.secFinancial')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field id="p-currency" label={t('partners.fCurrency')}>
            {/* BR-MD-01-05: the server refuses a change once documents exist. */}
            <NativeSelect id="p-currency" {...form.register('currency')}>
              {currencyCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {/* MD-01 field 22 — "Pick from list (Payment terms)"; MD-09 §9:
              with the org switch off, the concept does not appear at all and
              invoices fall due on issue. An inactive term a partner already
              carries still renders (BR-MD-01-07's offer/display line). */}
          {paymentTermsOn ? (
            <Field id="p-terms" label={t('partners.fTerms')}>
              <NativeSelect id="p-terms" {...form.register('paymentTermId')}>
                <option value="">{t('partners.termsNone')}</option>
                {(paymentTerms ?? [])
                  .filter((term) => term.isActive || term.id === form.watch('paymentTermId'))
                  .map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.name}
                    </option>
                  ))}
              </NativeSelect>
            </Field>
          ) : null}
          <Field id="p-credit" label={t('partners.fCreditLimit')} error={errors.creditLimit?.message}>
            <Input
              id="p-credit"
              dir="ltr"
              inputMode="decimal"
              placeholder="0.00"
              className="font-mono"
              aria-invalid={Boolean(errors.creditLimit)}
              {...form.register('creditLimit')}
            />
          </Field>
          <Field id="p-enforcement" label={t('partners.fEnforcement')}>
            <NativeSelect id="p-enforcement" {...form.register('creditEnforcement')}>
              <option value="block">{t('partners.enfBlock')}</option>
              <option value="warn">{t('partners.enfWarn')}</option>
              <option value="none">{t('partners.enfNone')}</option>
            </NativeSelect>
          </Field>
          <Field id="p-discount" label={t('partners.fDiscount')} error={errors.discountPct?.message}>
            {/* BR-MD-01-10a: capped at the company-wide 15% by the server. */}
            <Input
              id="p-discount"
              dir="ltr"
              inputMode="decimal"
              placeholder="0.00"
              className="font-mono"
              aria-invalid={Boolean(errors.discountPct)}
              {...form.register('discountPct')}
            />
          </Field>
          {/* MD-01 field 26 — a PICK over MD-14's list (BR-MD-01-16: active
              tiers only OFFERED; a switched-off tier already carried still
              DISPLAYS, the BR-MD-01-07 offer/display line). */}
          <Field id="p-tier" label={t('partners.fPriceTier')}>
            <NativeSelect id="p-tier" {...form.register('priceTierId')}>
              <option value="">{t('partners.tierNone')}</option>
              {(priceTiers ?? [])
                .filter((tier) => tier.isActive || tier.id === form.watch('priceTierId'))
                .map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))}
            </NativeSelect>
          </Field>
          <Field id="p-method" label={t('partners.fPaymentMethod')}>
            <NativeSelect id="p-method" {...form.register('paymentMethod')}>
              <option value="">—</option>
              {PAYMENT_METHODS.map((method) => (
                <option key={method} value={method}>
                  {t(partnerMethodKey(method) ?? method)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {/* MD-01 field 28: shown only when Supplier is ticked. */}
          {isSupplier ? (
            <Field id="p-lead" label={t('partners.fLeadTime')} error={errors.leadTimeDays?.message}>
              <Input
                id="p-lead"
                dir="ltr"
                inputMode="numeric"
                aria-invalid={Boolean(errors.leadTimeDays)}
                {...form.register('leadTimeDays')}
              />
            </Field>
          ) : null}
          <Field id="p-opening" label={t('partners.fOpeningBalance')} error={errors.openingBalance?.message}>
            {/* BR-MD-01-06: locked by the server once documents exist. */}
            <Input
              id="p-opening"
              dir="ltr"
              inputMode="decimal"
              placeholder="0.00"
              className="font-mono"
              aria-invalid={Boolean(errors.openingBalance)}
              {...form.register('openingBalance')}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('partners.secNotes')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            aria-label={t('partners.secNotes')}
            maxLength={2000}
            placeholder={t('partners.notesPh')}
            {...form.register('notes')}
          />
        </CardContent>
      </Card>

      {/* INV-F3 — the customer's branches, saved through their own endpoints
          once the partner exists (a new partner's card says "save first"). */}
      <PartnerBranchesCard partnerId={partnerId} />

      {/* INV-H5 — files on the partner (a contract, a registration). */}
      <AttachmentsPanel kind="partner" entityId={partnerId} />
    </form>
  );
}
